import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getValueFromSearchParam } from '../../utils/url';
import { useMemo, useState } from 'react';
import { publishKatalMetric } from '../../components/katalAnalytics';
import { MultiselectProps } from '@amzn/awsui-components-react/polaris/multiselect/interfaces';

import { parseTime, formatMinutes } from '../../components/shifts/timeModal';
import { useShiftFromUrlParams } from '../shifts/shiftFromUrlParams';
import { useUser } from '../user';
import { calculateShiftTime } from '../shifts/shiftTime';
import { useTimezone } from '../timezone';
import { LocationType, Source } from '@amzn/red-velvet-api';

const selectFilters = [
  'activity_location_type',
  'focus',
  'activity',
  'date',
  'elg_sponsors',
] as const;
const textFilters = [
  'title',
  'length',
  'organization_point_of_contact_name',
  'organization_point_of_contact_email',
  'engagement_recap',
  'photo_upload',
  'offsite_free_text',
  'building_code',
] as const;
const multiselectFilters = ['registered_community_ambassadors'] as const;
const autocompleteFilters = ['community_organization'] as const;

const editableInfoFields = [
  ...selectFilters,
  ...textFilters,
  ...multiselectFilters,
  ...autocompleteFilters,
] as const;

export type EditableInfoField = (typeof editableInfoFields)[number];

export function optionsToValueArrayString(options: readonly MultiselectProps.Option[]) {
  return `[${options.map((selectedOption) => selectedOption.value).join(',')}]`;
}

export function updateParams(
  params: URLSearchParams,
  editableInfo: { [s in EditableInfoField]: string },
  apiValues?: { [s in EditableInfoField]: string }
) {
  editableInfoFields.forEach((field) => {
    if (
      editableInfo[field] == null ||
      editableInfo[field] === '' ||
      editableInfo[field] === '[]' ||
      (apiValues && editableInfo[field] === apiValues[field])
    ) {
      params.delete(field);
    } else {
      params.set(field, editableInfo[field]);
      publishKatalMetric('editableEvent_' + field, editableInfo[field]);
    }
  });

  return params;
}

export function useEditableEventInfo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlEvent = useUrlEvent(searchParams);

  // Convert the event's locationType to the proper enum type
  const eventWithCorrectLocationType = useMemo(() => {
    return {
      ...urlEvent.event,
      location: {
        ...urlEvent.event.location,
        locationType: urlEvent.event.location.locationType as LocationType,
      },
    };
  }, [urlEvent.event]);

  const saveEventToUrl = () => {
    setSearchParams((params: URLSearchParams) => {
      return updateParams(params, urlEvent.editableInfo, urlEvent.defaultsFromApi);
    });
  };

  return {
    ...urlEvent,
    event: eventWithCorrectLocationType,
    saveEventToUrl,
    updateParams,
  };
}

function isValidWorkDocsURL(inputString: string) {
  try {
    new URL(inputString);
    return inputString.includes('amazon.awsapps.com/workdocs');
  } catch (error) {
    return false;
  }
}

export function useUrlEvent(searchParams: URLSearchParams) {
  const { t } = useTranslation(['shifts', 'translation']);
  const user = useUser();
  const timezone = useTimezone();

  const { event, shift, shifts, isFetched } = useShiftFromUrlParams();

  const initalState: { [s in EditableInfoField]: string } = {
    title: getValueFromSearchParam(searchParams, 'title'),
    date: getValueFromSearchParam(searchParams, 'date'),
    length: getValueFromSearchParam(searchParams, 'length'),
    focus: getValueFromSearchParam(searchParams, 'focus'),
    activity: getValueFromSearchParam(searchParams, 'activity'),
    activity_location_type: getValueFromSearchParam(searchParams, 'activity_location_type'),
    registered_community_ambassadors: getValueFromSearchParam(
      searchParams,
      'registered_community_ambassadors'
    ),
    community_organization: getValueFromSearchParam(searchParams, 'community_organization'),
    organization_point_of_contact_name: getValueFromSearchParam(
      searchParams,
      'organization_point_of_contact_name'
    ),
    organization_point_of_contact_email: getValueFromSearchParam(
      searchParams,
      'organization_point_of_contact_email'
    ),
    engagement_recap: getValueFromSearchParam(searchParams, 'engagement_recap'),
    elg_sponsors: getValueFromSearchParam(searchParams, 'elg_sponsors'),
    photo_upload: getValueFromSearchParam(searchParams, 'photo_upload'),
    offsite_free_text: getValueFromSearchParam(searchParams, 'offsite_free_text'),
    building_code: getValueFromSearchParam(searchParams, 'building_code'),
  };

  const [editableInfo, setEventValues] = useState(initalState);

  const errorCheckers: { [s in EditableInfoField]: (value: string) => string | undefined } = {
    title: (value: string) => (!value ? t('editableEvent.noTitle') : undefined),
    date: (value: string) => {
      const selectedDate = new Date(value + 'T00:00');
      const selectedEpochMills = selectedDate.getTime();
      const isInFuture = selectedEpochMills > Date.now();
      return isInFuture ? t('editableEvent.dateInFuture') : undefined;
    },
    length: (value: string) => {
      // Set default shift time to 0 so we don't get the entered time exceeded short shift time warning
      const parsedTime = parseTime((value.match(/[\d:]/g) || []).join(''), 0);
      return parsedTime.error ? t(parsedTime.error) : undefined;
    },
    focus: (value: string) => (!value ? t('editableEvent.noFocus') : undefined),
    activity: (value: string) => (!value ? t('editableEvent.noActivity') : undefined),
    activity_location_type: (value: string) =>
      !value ? t('editableEvent.noLocationType') : undefined,
    registered_community_ambassadors: (value: string) => {
      const ambassadors =
        value && value !== '[]'
          ? value
              .slice(1, -1)
              .split(',')
              .filter((ambassador) => ambassador.trim() !== '')
          : [];
      const inputtedAmbassadorTooLong = ambassadors.find((ambassador) => ambassador.length > 100);
      if (inputtedAmbassadorTooLong) {
        return t('editableEvent.inputtedAmbassadorTooLong', {
          length: inputtedAmbassadorTooLong.length,
        });
      }
      if (ambassadors.length > 10) {
        return t('editableEvent.tooManyAmbassadors');
      }
      return undefined;
    },
    community_organization: (value: string) =>
      value.length > 180
        ? t('editableEvent.inputTooLong', { maxLength: 180, length: value.length })
        : undefined,
    organization_point_of_contact_name: () => undefined,
    organization_point_of_contact_email: (value: string) =>
      value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? t('editableEvent.invalidEmail')
        : undefined,
    engagement_recap: (value: string) =>
      value.length > 5000
        ? t('editableEvent.inputTooLong', { maxLength: 5000, length: value.length })
        : undefined,
    elg_sponsors: () => undefined,
    photo_upload: (value) =>
      value && !isValidWorkDocsURL(value) ? t('editableEvent.invalidPhotoUpload') : undefined,
    // allowing no validation freeform as we want users to be able ot input locations
    // where countries might not have a state/province and cities not returned in API
    offsite_free_text: () => undefined,
    building_code: () => undefined,
  };
  const [eventErrors, setEventErrors] = useState<{ [s in EditableInfoField]?: string }>({});

  const warningCheckers: { [s in EditableInfoField]: (value: string) => string | undefined } = {
    title: () => undefined,
    date: () => undefined,
    length: (value: string) => {
      // Set default shift time to 0 so we don't get the entered time exceeded short shift time warning
      const parsedTime = parseTime((value.match(/[\d:]/g) || []).join(''), 0);
      return parsedTime.warning ? t(parsedTime.warning) : undefined;
    },
    focus: () => undefined,
    activity: () => undefined,
    activity_location_type: () => undefined,
    registered_community_ambassadors: () => undefined,
    community_organization: () => undefined,
    organization_point_of_contact_name: () => undefined,
    organization_point_of_contact_email: () => undefined,
    engagement_recap: () => undefined,
    elg_sponsors: () => undefined,
    photo_upload: () => undefined,
    offsite_free_text: () => undefined,
    building_code: () => undefined,
  };
  const [eventWarnings, setEventWarnings] = useState<{ [s in EditableInfoField]?: string }>({});

  const updateEditableInfoFields = (fields: { [s in EditableInfoField]?: string }) => {
    validateFields(fields);
    setEventValues({
      ...editableInfo,
      ...fields,
    });
  };

  const defaultsFromApi = {
    title: shift?.name || event?.title || '',
    activity_location_type: event
      ? event?.location.locationType === 'VIRTUAL'
        ? 'virtual'
        : event?.location.amazonBuilding
          ? 'on-campus'
          : 'off-campus'
      : '',
    focus: event?.focusArea || '',
    activity: event?.primaryActivity || '',
    registered_community_ambassadors: event?.ambassadors ? `[${event?.ambassadors.join(',')}]` : '',
    organization_point_of_contact_name: event?.orgPOCName || '',
    organization_point_of_contact_email: event?.orgPOCEmail || '',
    engagement_recap: event?.recap || '',
    elg_sponsors: event?.sponsors || '',
    date:
      shift?.startDateTime?.toISOString().split('T')[0] ||
      event?.startTimestamp?.toISOString().split('T')[0] ||
      '',
    length: shift ? formatMinutes(calculateShiftTime(shift)) : '',
    community_organization: event?.communityOrg || '',
    photo_upload: event?.photosUploadUrl || '',
    offsite_free_text: event?.location.address_string ? event.location.address_string : '',
    building_code: event?.location.amazonBuilding || '',
  };

  // Because we ONLY want to use defaults if the user has not entered any values, we need to do manual || collapse on each attribute instead of the spread operator
  const combinedEditableAndApiData = {
    title: editableInfo.title || defaultsFromApi.title,
    activity_location_type:
      editableInfo.activity_location_type || defaultsFromApi.activity_location_type,
    focus: editableInfo.focus || defaultsFromApi.focus,
    activity: editableInfo.activity || defaultsFromApi.activity,
    registered_community_ambassadors:
      editableInfo.registered_community_ambassadors ||
      defaultsFromApi.registered_community_ambassadors,
    organization_point_of_contact_name:
      editableInfo.organization_point_of_contact_name ||
      defaultsFromApi.organization_point_of_contact_name,
    organization_point_of_contact_email:
      editableInfo.organization_point_of_contact_email ||
      defaultsFromApi.organization_point_of_contact_email,
    engagement_recap: editableInfo.engagement_recap || defaultsFromApi.engagement_recap,
    elg_sponsors: editableInfo.elg_sponsors || defaultsFromApi.elg_sponsors,
    date:
      editableInfo.date || defaultsFromApi.date || new Date(Date.now()).toISOString().split('T')[0],
    length: editableInfo.length || defaultsFromApi.length || '',
    community_organization:
      editableInfo.community_organization || defaultsFromApi.community_organization,
    photo_upload: editableInfo.photo_upload || defaultsFromApi.photo_upload,
    offsite_free_text: editableInfo.offsite_free_text || defaultsFromApi.offsite_free_text,
    building_code: editableInfo.building_code || defaultsFromApi.building_code,
  };

  const hasChanges = editableInfoFields.some((field) => editableInfo[field]);

  const validateFields = (newValues: { [s in EditableInfoField]?: string }): boolean => {
    const errors: { [s in EditableInfoField]?: string } = { ...eventErrors };
    const warnings: { [s in EditableInfoField]?: string } = { ...eventWarnings };

    (Object.keys(newValues) as Array<EditableInfoField>).forEach((field) => {
      const fieldValue = newValues[field];

      //Cleans up error messaging if input is removed for optional community organization or engagement recap
      if (
        fieldValue === '' &&
        (field === 'community_organization' ||
          field === 'engagement_recap' ||
          field === 'registered_community_ambassadors')
      ) {
        errors[field] = undefined;
        warnings[field] = undefined;
      } else {
        const valueToValidate = fieldValue || combinedEditableAndApiData[field];
        errors[field] = errorCheckers[field](valueToValidate);
        warnings[field] = warningCheckers[field](valueToValidate);
      }
    });
    setEventErrors(errors);
    setEventWarnings(warnings);

    return (
      (Object.keys(errors) as Array<EditableInfoField>).filter((key) => errors[key]).length === 0
    );
  };

  const combinedStartDateForUnlistedEvent = new Date(combinedEditableAndApiData.date); // Unlisted event startDateTime should always default to 00:00AM UTC
  const combinedEndTimeForShift = new Date(combinedStartDateForUnlistedEvent);
  const shiftLength = parseTime(
    (combinedEditableAndApiData.length.match(/[\d:]/g) || []).join(''),
    0
  ).minutesReported;
  combinedEndTimeForShift.setMinutes(combinedStartDateForUnlistedEvent.getMinutes() + shiftLength);

  const isGivehubEvent = event && event.source === Source.GIVEHUB;
  const endTimeForUnlistedEvent = new Date(combinedStartDateForUnlistedEvent);
  endTimeForUnlistedEvent.setUTCHours(23, 59, 0, 0); // Unlisted event endDateTime should always default to 23:59PM UTC of the same date of activity

  return {
    editableInfo: combinedEditableAndApiData,
    defaultsFromApi,
    event: {
      ...event,
      title: event?.eventId
        ? event?.title || combinedEditableAndApiData.title
        : combinedEditableAndApiData.title,
      description:
        event?.description ?? 'This event was created in Volunteer Center to track attendance',
      location: {
        ...event?.location,
        locationType: combinedEditableAndApiData.activity_location_type
          ? combinedEditableAndApiData.activity_location_type === 'virtual'
            ? 'VIRTUAL'
            : 'PHYSICAL'
          : event?.location.locationType === 'VIRTUAL'
            ? 'VIRTUAL'
            : 'PHYSICAL',
        amazonBuilding:
          combinedEditableAndApiData.activity_location_type === 'on-campus'
            ? combinedEditableAndApiData.building_code
            : undefined,
        address_string:
          combinedEditableAndApiData.activity_location_type === 'off-campus'
            ? combinedEditableAndApiData.offsite_free_text
            : undefined,
      },
      rvSignupSupported: true,
      timeZone: timezone,
      startTimestamp: isGivehubEvent ? event.startTimestamp : combinedStartDateForUnlistedEvent,
      endTimestamp: isGivehubEvent ? event.endTimestamp : endTimeForUnlistedEvent,
      skills: event?.skills || [],
      source: event?.source || 'VC',
      // Do NOT use the eventId from the url, as it may not actually represent a valid id in the database
      eventId: event?.eventId || '',
      // Hacky workaround for owner defaults based on how Benevity stores owner data
      ownerEmail: event?.ownerEmail || user.userId + '@amazon.com',
      focusArea: combinedEditableAndApiData.focus,
      primaryActivity: combinedEditableAndApiData.activity,
      ambassadors:
        combinedEditableAndApiData.registered_community_ambassadors.length === 0
          ? []
          : combinedEditableAndApiData.registered_community_ambassadors.slice(1, -1).split(','),
      communityOrg: combinedEditableAndApiData.community_organization,
      orgPOCName: combinedEditableAndApiData.organization_point_of_contact_name,
      orgPOCEmail: combinedEditableAndApiData.organization_point_of_contact_email,
      recap: combinedEditableAndApiData.engagement_recap,
      sponsors: combinedEditableAndApiData.elg_sponsors,
      photosUploadUrl: combinedEditableAndApiData.photo_upload,
    },
    shift: {
      ...shift,
      // Do NOT use the shiftId or eventId from the url, as it may not actually represent a valid id in the database
      shiftId: shift?.shiftId || '',
      eventId: event?.eventId || '',
      name: combinedEditableAndApiData.title,
      maxSignup: shift?.maxSignup || 0,
      signupsRemaining: shift?.signupsRemaining || 0,
      startDateTime: isGivehubEvent ? shift?.startDateTime : combinedStartDateForUnlistedEvent,
      endDateTime: isGivehubEvent ? shift?.endDateTime : combinedEndTimeForShift,
      complete: shift?.complete || true,
      shiftLength,
      source: shift?.source || Source.VC,
    },
    shifts,
    isFetched,
    hasChanges,
    eventErrors,
    eventWarnings,
    validateFields,
    updateEditableInfoFields,
  };
}
